(function () {
  const config = window.ITMartFacetTaxonomy || {};
  const filterContainerSelector = '#main-collection-filters, #main-search-filters';
  let taxonomyPromise;
  let categoryDataPromise;
  let enhanceTimer = null;
  let observerBound = false;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function fetchJson(url) {
    if (!url) {
      return Promise.resolve(null);
    }

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unable to load JSON: ' + url);
        }

        return response.json();
      })
      .catch(function (error) {
        console.error(error);
        return null;
      });
  }

  function fetchTaxonomy() {
    if (taxonomyPromise) {
      return taxonomyPromise;
    }

    taxonomyPromise = fetchJson(config.taxonomyUrl).then(function (payload) {
      if (!payload) {
        return null;
      }

      const filterLookup = {};

      (payload.filters || []).forEach(function (filterDef) {
        (filterDef.aliases || []).forEach(function (alias) {
          const key = normalize(alias);

          if (!key || filterLookup[key]) {
            return;
          }

          filterLookup[key] = filterDef;
        });
      });

      payload.filterLookup = filterLookup;
      return payload;
    });

    return taxonomyPromise;
  }

  function fetchCategoryData() {
    if (categoryDataPromise) {
      return categoryDataPromise;
    }

    categoryDataPromise = fetchJson(config.categoryDataUrl).then(function (payload) {
      if (!payload) {
        return null;
      }

      const collectionLookup = {};

      (payload.top_categories || []).forEach(function (topCategory) {
        (topCategory.subcategories || []).forEach(function (subcategory) {
          (subcategory.final_categories || []).forEach(function (finalCategory) {
            collectionLookup[finalCategory.collection_handle] = topCategory.slug;
          });

          if (topCategory.is_flat_category && subcategory.collection_handle) {
            collectionLookup[subcategory.collection_handle] = topCategory.slug;
          }
        });
      });

      payload.collectionLookup = collectionLookup;
      return payload;
    });

    return categoryDataPromise;
  }

  function getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function resolveProfile(taxonomy, categoryData) {
    if (!taxonomy || !taxonomy.profiles) {
      return null;
    }

    if (/\/search(?:\/|$)/i.test(window.location.pathname)) {
      return taxonomy.profiles['marketplace-search'] || null;
    }

    const collectionHandle = getCollectionHandle();

    if (!collectionHandle || !categoryData || !categoryData.collectionLookup) {
      return null;
    }

    const topCategorySlug = categoryData.collectionLookup[collectionHandle];
    return topCategorySlug ? taxonomy.profiles[topCategorySlug] || null : null;
  }

  function getFacetRoots(scope) {
    return Array.prototype.slice
      .call(scope.querySelectorAll('.js-filter[data-facet-label]'))
      .filter(function (element) {
        if (!element.querySelector('summary') || !element.parentElement) {
          return false;
        }

        if (element.classList.contains('mobile-facets__details')) {
          return false;
        }

        if (element.closest('menu-drawer')) {
          return false;
        }

        return true;
      });
  }

  function getFacetLabel(root) {
    const dataLabel = root.getAttribute('data-facet-label');
    if (dataLabel) {
      return dataLabel;
    }

    const summaryLabel = root.querySelector('.facets__summary-label');
    if (summaryLabel) {
      return summaryLabel.childNodes[0] ? summaryLabel.childNodes[0].textContent : summaryLabel.textContent;
    }

    const genericLabel = root.querySelector('summary span');
    return genericLabel ? genericLabel.textContent : '';
  }

  function setLeadingText(container, nextText) {
    if (!container) {
      return;
    }

    const normalizedText = String(nextText || '').trim();

    for (let index = 0; index < container.childNodes.length; index += 1) {
      const child = container.childNodes[index];

      if (child.nodeType === Node.TEXT_NODE) {
        child.textContent = normalizedText;
        return;
      }
    }

    container.insertBefore(document.createTextNode(normalizedText), container.firstChild || null);
  }

  function applyDisplayLabel(root, filterDef) {
    const summaryLabel = root.querySelector('.facets__summary-label');

    if (summaryLabel) {
      setLeadingText(summaryLabel, filterDef.display_label);
      return;
    }

    const simpleLabel = root.querySelector('summary > div > span');
    if (simpleLabel && !simpleLabel.closest('.mobile-facets__arrow')) {
      simpleLabel.textContent = filterDef.display_label;
    }
  }

  function setupValueSearch(root, filterDef) {
    if (root.hasAttribute('data-enterprise-search-ready')) {
      return;
    }

    const list = root.querySelector('.facets__list, .facets__list--vertical, .mobile-facets__list');

    if (!list) {
      return;
    }

    const items = Array.prototype.slice.call(list.children).filter(function (item) {
      return item.querySelector('.facet-checkbox__text-label');
    });

    if (!items.length) {
      return;
    }

    if (!filterDef.searchable_values && items.length < 10) {
      return;
    }

    const host = list.parentElement;
    if (!host || host.querySelector('.enterprise-facet-search')) {
      root.setAttribute('data-enterprise-search-ready', 'true');
      return;
    }

    const wrapper = document.createElement('div');
    const input = document.createElement('input');
    const empty = document.createElement('p');

    wrapper.className = 'enterprise-facet-search';
    input.className = 'enterprise-facet-search__input';
    input.type = 'search';
    input.placeholder = 'Search options';
    input.setAttribute('aria-label', filterDef.display_label + ' search');

    empty.className = 'enterprise-facet-search__empty';
    empty.hidden = true;
    empty.textContent = 'No matching options.';

    wrapper.appendChild(input);
    host.insertBefore(wrapper, list);
    host.insertBefore(empty, list.nextSibling);

    function updateList() {
      const query = normalize(input.value);
      let visibleCount = 0;

      items.forEach(function (item) {
        const label = item.querySelector('.facet-checkbox__text-label');
        const matches = !query || normalize(label ? label.textContent : '').indexOf(query) > -1;
        item.hidden = !matches;

        if (matches) {
          visibleCount += 1;
        }
      });

      empty.hidden = visibleCount > 0;
    }

    input.addEventListener('input', updateList);
    root.setAttribute('data-enterprise-search-ready', 'true');
  }

  function reorderFacetRoots(roots, profile) {
    if (!roots.length || !profile || !profile.filter_ids) {
      return;
    }

    const orderLookup = {};
    profile.filter_ids.forEach(function (filterId, index) {
      orderLookup[filterId] = index;
    });

    const parentGroups = new Map();

    roots.forEach(function (root, index) {
      if (!root.parentElement) {
        return;
      }

      const parent = root.parentElement;
      const currentGroup = parentGroups.get(parent) || [];
      const filterId = root.getAttribute('data-enterprise-filter-id') || '';
      const orderIndex = Object.prototype.hasOwnProperty.call(orderLookup, filterId) ? orderLookup[filterId] : 1000 + index;

      currentGroup.push({
        root: root,
        orderIndex: orderIndex,
        originalIndex: index
      });
      parentGroups.set(parent, currentGroup);
    });

    parentGroups.forEach(function (groupRoots, parent) {
      if (!groupRoots.length) {
        return;
      }

      const sortedRoots = groupRoots.sort(function (left, right) {
        if (left.orderIndex !== right.orderIndex) {
          return left.orderIndex - right.orderIndex;
        }

        return left.originalIndex - right.originalIndex;
      });

      const marker = document.createComment('enterprise-facet-order');
      parent.insertBefore(marker, sortedRoots[0].root);

      let cursor = marker;
      sortedRoots.forEach(function (entry) {
        parent.insertBefore(entry.root, cursor.nextSibling);
        cursor = entry.root;
      });

      parent.removeChild(marker);
    });
  }

  function enhanceFacetScope(scope, taxonomy, profile) {
    const roots = getFacetRoots(scope);

    if (!roots.length) {
      return;
    }

    roots.forEach(function (root) {
      const currentLabel = getFacetLabel(root);
      const filterDef = taxonomy.filterLookup[normalize(currentLabel)];

      if (!filterDef) {
        return;
      }

      root.setAttribute('data-enterprise-filter-id', filterDef.id);
      root.setAttribute('data-enterprise-filter-group', filterDef.group_id);
      root.setAttribute('data-enterprise-filter-priority', String(filterDef.priority));

      applyDisplayLabel(root, filterDef);
      setupValueSearch(root, filterDef);
    });

    reorderFacetRoots(roots, profile);
  }

  function enhancePage() {
    if (!document.querySelector(filterContainerSelector)) {
      return;
    }

    Promise.all([fetchTaxonomy(), fetchCategoryData()]).then(function (responses) {
      const taxonomy = responses[0];
      const categoryData = responses[1];

      if (!taxonomy) {
        return;
      }

      const profile = resolveProfile(taxonomy, categoryData);
      if (!profile) {
        return;
      }

      document.querySelectorAll(filterContainerSelector).forEach(function (container) {
        enhanceFacetScope(container, taxonomy, profile);
      });
    });
  }

  function scheduleEnhancement() {
    window.clearTimeout(enhanceTimer);
    enhanceTimer = window.setTimeout(enhancePage, 40);
  }

  function bindObserver() {
    if (observerBound || !document.body) {
      return;
    }

    const observer = new MutationObserver(function (mutations) {
      const shouldRun = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === Node.ELEMENT_NODE;
        });
      });

      if (shouldRun) {
        scheduleEnhancement();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    observerBound = true;
  }

  function init() {
    if (!config.taxonomyUrl) {
      return;
    }

    scheduleEnhancement();
    bindObserver();
    window.addEventListener('popstate', scheduleEnhancement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
